// src/pages/widgets/WidgetTemplateEditorPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  NavigateNext as NavigateNextIcon,
  Code as EmbedIcon, // Added
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { widgetService } from '../../api/widgetService';
import AllowedOriginsManager from '../../components/widgets/AllowedOriginsManager';
import EmbedCodeModal from '../../components/widgets/EmbedCodeModal'; // Added
import IdDisplayWithCopy from '../../components/common/IdDisplayWithCopy'; // Import

const WidgetTemplateEditorPage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const location = useLocation();
  const isEditing = !!templateId;

  const [formData, setFormData] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // State for EmbedCodeModal
  const [embedModalProps, setEmbedModalProps] = useState({
    open: false,
    versionId: null,
    templateId: null,
    templateName: null,
  });

  const prefillAgentIdForVersion = location.state?.prefillAgentIdForVersion;
  const cameFromAgentContext = location.state?.fromAgentContext;

  useEffect(() => {
    if (!isEditing || !templateId) {
      setLoading(false);
      return;
    }

    const fetchTemplate = async () => {
      setLoading(true);
      setError(null);
      setFormErrors({});

      const res = await widgetService.getTemplate(templateId);
      if (res.isError) {
        setError(res.message || 'Failed to load template.');
        toast.error(res.message || 'Failed to load template.');
      } else {
        setFormData({
          name: res.name || '',
          description: res.description || '',
        });
      }
      setLoading(false);
    };
    fetchTemplate();
  }, [isEditing, templateId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError(null);
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Template name is required.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFormErrors({});

    if (!validateForm()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setSaving(true);
    let res;
    const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
    };

    if (isEditing) {
      res = await widgetService.updateTemplate(templateId, payload);
    } else {
      res = await widgetService.createTemplate(payload);
    }
    setSaving(false);

    if (res.isError) {
      setError(res.message || 'Save failed.');
      toast.error(res.message || 'Save failed.');
    } else {
      toast.success(`Widget template ${isEditing ? 'updated' : 'created'} successfully.`);
      
      const newTemplateId = isEditing ? templateId : res.id;

      if (!isEditing && res.id) {
        if (prefillAgentIdForVersion) {
          navigate(`/widgets/${newTemplateId}/versions/new`, { 
            state: { prefillAgentId: prefillAgentIdForVersion, fromTemplateCreation: true } 
          });
        } else {
          navigate(`/widgets/${newTemplateId}/edit`, { replace: true }); // Replace history to avoid back to "new"
        }
      } else if (isEditing) {
        // Optionally, navigate back or stay. For now, stay.
        // To refresh data if needed (e.g., if name changed for breadcrumbs):
        // fetchTemplate(); // Or rely on navigation to list page if user clicks back
      }
    }
  };

  const handleOpenEmbedModalForCurrentTemplate = () => {
    if (isEditing && templateId && formData.name) {
      setEmbedModalProps({
        open: true,
        versionId: null, // From here, we primarily offer embedding the template itself
        templateId: templateId,
        templateName: formData.name,
      });
    } else if (!isEditing) {
      toast.info("Please create and save the template first to get its embed code.");
    }
  };

  const handleCloseEmbedModal = () => {
    setEmbedModalProps({ open: false, versionId: null, templateId: null, templateName: null });
  };

  const handleBackNavigation = () => {
    if (cameFromAgentContext && prefillAgentIdForVersion) {
      navigate(`/agents/${prefillAgentIdForVersion}/widgets`);
    } else {
      navigate('/widgets');
    }
  };


  if (loading && isEditing) { // Show loader only when editing and fetching
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{p: 5, height: 'calc(100vh - 200px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = isEditing ? `Edit: ${formData.name || 'Template'}` : 'Create New Template';
  const breadcrumbCurrent = isEditing ? (formData.name || 'Loading Template...') : 'Create New Template';


  return (
    <Paper sx={{ p: {xs: 2, sm: 3}, maxWidth: 1000, mx: 'auto' }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/widgets">
          Widget Templates
        </MuiLink>
        {isEditing && templateId && (
             <MuiLink component={RouterLink} underline="hover" color="inherit" to={`/widgets/${templateId}/versions`}>
                {formData.name || `Template ...${templateId.slice(-4)}`}
            </MuiLink>
        )}
        <Typography color="text.primary">{isEditing ? 'Settings' : 'Create New Template'}</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEditing ? 'Widget Template Settings' : 'Create New Widget Template'}
        </Typography>
        {isEditing && templateId && (
             <Stack direction="row" spacing={1}>
                <IdDisplayWithCopy id={templateId} label="Template ID" /> {/* DISPLAY ID HERE */}
                <Button 
                    variant="outlined" 
                    startIcon={<EmbedIcon />} 
                    onClick={handleOpenEmbedModalForCurrentTemplate}
                    disabled={saving || loading || !formData.name}
                >
                    Get Embed Code
                </Button>
             </Stack>
        )}
      </Stack>


      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          label="Template Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          margin="normal"
          disabled={saving}
          error={!!formErrors.name}
          helperText={formErrors.name || "A unique name for this widget template (e.g., 'AI Chat Bubble', 'Support Form')."}
          inputProps={{ maxLength: 100 }}
        />
        <TextField
          fullWidth
          label="Description (Optional)"
          name="description"
          value={formData.description}
          onChange={handleChange}
          margin="normal"
          multiline
          rows={3}
          disabled={saving}
          helperText="Briefly describe what this template is for or its general purpose."
          inputProps={{ maxLength: 500 }}
        />

        <Stack direction="row" justifyContent="space-between" mt={3} spacing={2}>
          <Button
            variant="outlined"
            onClick={handleBackNavigation}
            startIcon={<BackIcon />}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={
              saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
            }
            disabled={saving || loading} // Disable if initial data is loading too
          >
            {saving
              ? 'Saving...'
              : isEditing
              ? 'Save Template Changes'
              : 'Create Template & Continue'}
          </Button>
        </Stack>
      </Box>

      {isEditing && templateId && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" mb={2}>Allowed Origins</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Specify the hostnames (e.g., <code>www.example.com</code>, <code>app.mysite.io</code>, <code>localhost:3000</code>) where widgets using this template are permitted to run.
          </Typography>
          <AllowedOriginsManager templateId={templateId} />
        </>
      )}
      {!isEditing && (
         <Alert severity="info" sx={{mt: 3}}>
            Allowed origins and other settings can be managed after the template is created.
         </Alert>
      )}

      <EmbedCodeModal
        open={embedModalProps.open}
        onClose={handleCloseEmbedModal}
        versionId={embedModalProps.versionId}
        templateId={embedModalProps.templateId}
        templateName={embedModalProps.templateName}
      />
    </Paper>
  );
};

export default WidgetTemplateEditorPage;